import { L1ObjectChannel, L1ObjectObjective } from 'core/l1Object/L1Object';
import {
  CampaignAdTypeState,
  ChannelGroupDTO,
  ChannelGroupStatus,
  ChannelType,
  DynamicCreativeTypeSetting,
  DeliveryUnit,
  ScreenDisplayType
} from './ChannelGroup';
import { ChannelGroupManager } from './ChannelGroupManager';
import { CHANNEL } from 'core/agency/AddonFeature';
import { CreativeType } from 'core/creative/Creative';
import { AdType, RtbCampaignPlanType } from 'core/rtbCampaign/RtbCampaign';
import {
  ChannelGroupConfigBuilder,
  channelGroup,
  adType,
  banner,
  StaticCreativeTypeSetting as BuilderStaticCreativeTypeSetting,
  StaticAdTypeSetting as BuilderStaticAdTypeSetting,
  CampaignAdTypeSetting as BuilderCampaignAdTypeSetting,
  ChannelGroupConfig as BuilderChannelGroupConfig
} from './ChannelGroupSettingBuilder';

export type StaticCreativeTypeSetting = BuilderStaticCreativeTypeSetting;

export type StaticAdTypeSetting = BuilderStaticAdTypeSetting;

export type CampaignAdTypeSetting = BuilderCampaignAdTypeSetting;

export type CreativeTypeSetting =
  | DynamicCreativeTypeSetting
  | StaticCreativeTypeSetting;

export type ChannelGroupConfig = BuilderChannelGroupConfig;

// ============ Channel Group IDs ============

export const CHANNEL_GROUP_ID = {
  RTB: 0,
  PIC: 2,
  WI_FRIGE: 4
} as const;

export class ChannelGroupSetting {
  private static readonly _values: Map<string, ChannelGroupSetting> = new Map();
  private static readonly _creativeTypeSettings: Map<
    number,
    CreativeTypeSetting
  > = new Map();
  private static readonly _adTypeSettings: Map<string, CampaignAdTypeSetting> =
    new Map();

  // ECOPD-5738: 已下架的簡訊渠道（id 7）不在這裡。後端仍會以停用狀態回傳它，
  // 由 register() 註冊成動態渠道 —— 殘留資料照樣查得到（fromId 不會 throw），
  // 而 activeValues() 會把它擋在所有可挑選的清單外。
  private static readonly STATIC_CHANNEL_GROUP_IDS: number[] = [
    CHANNEL_GROUP_ID.RTB
  ];

  // ECOPD-5630: 內建 CBO 的渠道。static 渠道的 addonFeatures 在本 registry 恆為硬寫的 {}，
  // 讀不到 DB 的 supportCBO，所以它們能不能用 CBO 是寫死在這裡的。
  // ⚠️ 這份清單「不等於」STATIC_CHANNEL_GROUP_IDS —— 是不是 static 渠道跟有沒有內建 CBO
  //    是兩回事，目前剛好都只有 RTB，容易被誤當成同一件事。
  private static readonly CBO_BUILT_IN_CHANNEL_GROUP_IDS: number[] = [
    CHANNEL_GROUP_ID.RTB
  ];

  private constructor(
    public readonly key: string,
    public config: ChannelGroupConfig
  ) {
    ChannelGroupSetting._values.set(key, this);
    this.applySpecialRules();
  }

  private applySpecialRules(): void {
    const { config } = this;
    if (config.id === CHANNEL_GROUP_ID.PIC) {
      config.defaultMargin = 25;
      config.needExpressPass = true;
    } else if (config.id === CHANNEL_GROUP_ID.WI_FRIGE) {
      // ECOPD-5681：這裡原本還會硬塞一個「動態影片廣告」廣告類型（依天氣即時生成影片的
      // DVC 素材），是整套後端驅動渠道裡唯一的寫死逃生口 —— 冰櫃合作結束後已移除。
      // 剩下的 needExpressPass 與 DVC 無關，它決定代理商「銷售渠道」頁的冰櫃那列有沒有
      // 「素材是否屬於快速通關」開關，不要一起拔掉。
      config.needExpressPass = true;
    }
  }

  private static createFromBuilder(
    builder: ChannelGroupConfigBuilder,
    key: string
  ): ChannelGroupSetting {
    const { config } = builder.build(key);
    return new ChannelGroupSetting(key, config);
  }

  // ============ Static Channel Group Definitions ============

  static readonly RTB = ChannelGroupSetting.createFromBuilder(
    channelGroup(CHANNEL_GROUP_ID.RTB)
      .name('RTB')
      .channelType(ChannelType.RTB)
      .addon(CHANNEL.RTB)
      .objectives(
        L1ObjectObjective.AWARENESS,
        L1ObjectObjective.TRAFFIC,
        L1ObjectObjective.SALES
      )
      .adTypes(
        adType(AdType.DISPLAY)
          .withBannerTypes(
            banner(CreativeType.IMAGE, 'creativeType.image'),
            banner(CreativeType.NATIVE, 'creativeType.native'),
            banner(CreativeType.HTML5, 'creativeType.html5')
          )
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPC,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.DCPM
          ),
        adType(AdType.VIDEO)
          .withBannerTypes(banner(CreativeType.VIDEO, 'creativeType.video'))
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPC,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.FCPV,
            RtbCampaignPlanType.FVCPM,
            RtbCampaignPlanType.DCPM
          ),
        adType(AdType.COMBO)
          .withBannerTypes(
            banner(CreativeType.COMBO, 'creativeType.combo'),
            banner(CreativeType.HTML5, 'creativeType.html5')
          )
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPC,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.FCPV,
            RtbCampaignPlanType.FVCPM,
            RtbCampaignPlanType.DCPM
          ),
        adType(AdType.THIRD_PARTY)
          .withBannerTypes(
            banner(CreativeType.THIRD_PARTY, 'creativeType.thirdParty'),
            banner(CreativeType.HTML5, 'creativeType.html5')
          )
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPC,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.FCPV,
            RtbCampaignPlanType.FVCPM,
            RtbCampaignPlanType.DCPM
          ),
        adType(AdType.THIRD_PARTY_BOTTOM)
          .withBannerTypes(
            banner(CreativeType.THIRD_PARTY, 'creativeType.thirdParty'),
            banner(CreativeType.HTML5, 'creativeType.html5'),
            banner(CreativeType.CUSTOM_BOTTOM, 'creativeType.customBottom')
          )
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPC,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.FCPV,
            RtbCampaignPlanType.FVCPM,
            RtbCampaignPlanType.DCPM
          ),
        adType(AdType.THIRD_PARTY_RECTANGLE)
          .withBannerTypes(
            banner(CreativeType.THIRD_PARTY, 'creativeType.thirdParty'),
            banner(CreativeType.HTML5, 'creativeType.html5'),
            banner(
              CreativeType.CUSTOM_RECTANGLE,
              'creativeType.customRectangle'
            )
          )
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPC,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.FCPV,
            RtbCampaignPlanType.FVCPM,
            RtbCampaignPlanType.DCPM
          ),
        adType(AdType.SANDBOX_OUTDOOR)
          .withBannerTypes(
            banner(CreativeType.SANDBOX_OUTDOOR, 'creativeType.sandboxOutdoor')
          )
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.FCPV
          ),
        adType(AdType.SANDBOX_MOVABLE_OUTDOOR)
          .withBannerTypes(
            banner(CreativeType.SANDBOX_OUTDOOR, 'creativeType.sandboxOutdoor')
          )
          .withPricingModels(
            RtbCampaignPlanType.RS,
            RtbCampaignPlanType.FCPM,
            RtbCampaignPlanType.FCPV
          )
      ),
    L1ObjectChannel.RTB
  );

  static values(): ChannelGroupSetting[] {
    return Array.from(this._values.values());
  }

  /**
   * ECOPD-5566: 未停用的渠道。**所有「使用者可以挑選」的清單一律用這個**
   * （建立活動群組、PMP、經銷商 add-on 各分頁…），`values()` 留給
   * 查表 / 顯示既有資料的路徑 —— 停用渠道的既有群組、活動、素材仍要看得到。
   */
  static activeValues(): ChannelGroupSetting[] {
    return this.values().filter((setting) => !setting.isEffectivelyArchived());
  }

  static creativeTypeSettings(
    onlyActive = false
  ): Map<number, CreativeTypeSetting> {
    const adTypeSettings = this.values().flatMap((value) =>
      onlyActive
        ? value.getActiveCampaignAdTypes()
        : value.config.campaignAdTypeSettings
    );
    const creativeTypeSettings = adTypeSettings.flatMap(
      (setting) => setting.bannerTypes
    );
    const result = new Map();
    creativeTypeSettings.forEach((setting) => {
      result.set(setting.bannerTypeId, setting);
    });
    return result;
  }

  static adTypeSettings(
    onlyActive = false
  ): Map<string, CampaignAdTypeSetting> {
    const adTypeSettings = this.values().flatMap((value) =>
      onlyActive
        ? value.getActiveCampaignAdTypes()
        : value.config.campaignAdTypeSettings
    );
    const result = new Map();
    adTypeSettings.forEach((setting) => {
      const adTypeKey = setting.adTypeId
        ? setting.adTypeId.toString()
        : setting.name;
      result.set(adTypeKey, setting);
    });
    return result;
  }

  static fromKey(key: string): ChannelGroupSetting {
    const channelGroupSetting = this._values.get(key);
    if (!channelGroupSetting) {
      throw new Error(`ChannelGroupSetting not found for channel: ${key}`);
    }
    return channelGroupSetting;
  }

  /**
   * 依 channel group id 取設定，查不到回 `undefined`（不 throw），讓呼叫端自己決定
   * fallback。
   *
   * 與 `fromId` 的差別只在查不到時的行為。渲染路徑上不能用 `fromId`——電子紙、以及
   * 任何後台建出來的動態渠道，在 registry 尚未 init 完成前都查不到，`fromId` 會
   * 直接把整個畫面炸掉（ECOPD-5581）。
   */
  static findById(id: number): ChannelGroupSetting | undefined {
    return Array.from(this._values.values()).find(
      (setting) => setting.config?.id === id
    );
  }

  /**
   * 把各種來源的 channelGroupId 轉成可查詢的數字。
   *
   * ⚠️ 接受字串是因為有些來源（報表的 `dimensionID`、選項的 `value`）是字串。
   * **空字串要單獨擋掉**：`Number('')` 是 `0`，而 `0` 是 RTB 的 channelGroupId ——
   * 一筆空值會被當成 RTB，RTB 若停用就會把後綴貼到完全無關的東西上。
   * 其餘轉不出數字的值（`undefined`、`null`、非數字字串）走 NaN 這條。
   */
  private static toChannelGroupId(
    id: number | string | undefined | null
  ): number {
    return id === '' || id === null ? NaN : Number(id);
  }

  /**
   * ECOPD-5572: 指定渠道在 PMax 端是否停用（**合併判定**：RMax 已封存 OR PMax 自停）。
   *
   * 值由後端算好放在 `effectiveArchived`，這裡只是查表後照讀 ——
   * **不要**在任何呼叫端自己組 `status === ARCHIVED || pmaxArchived`。
   *
   * 查不到渠道時回 `false`（fail-open）：寫入面後端另有擋，而 fail-closed 會在
   * registry 還沒載入的空窗把所有畫面變成唯讀。寧可少加一個後綴 / 少擋一次編輯。
   */
  static isEffectivelyArchivedChannel(
    id: number | string | undefined | null
  ): boolean {
    const channelGroupId = this.toChannelGroupId(id);
    return Number.isFinite(channelGroupId)
      ? !!this.findById(channelGroupId)?.isEffectivelyArchived()
      : false;
  }

  /**
   * ECOPD-5566: 由素材類型（bannerTypeId）反查它所屬的渠道 id。
   *
   * ⚠️ 正確性建立在「一個素材類型只屬於一個渠道」這條領域事實上 ——
   * `creativeTypeSettings()` 是 last-write-wins，這條事實一旦不成立就會**無聲**取到
   * 錯的渠道。`ChannelGroupSettings.test.ts` 有一條測試釘住它。
   */
  static findChannelGroupIdByCreativeType(
    creativeType: number | undefined
  ): number | undefined {
    if (creativeType === undefined) {
      return undefined;
    }
    return this.creativeTypeSettings().get(creativeType)?.channelGroupId;
  }

  static fromId(id: number): ChannelGroupSetting {
    const channelGroupSetting = this.findById(id);
    if (!channelGroupSetting) {
      throw new Error(
        `ChannelGroupSetting not found for channel group id: ${id}`
      );
    }
    return channelGroupSetting;
  }

  static isStaticChannelGroup(id: number): boolean {
    return this.STATIC_CHANNEL_GROUP_IDS.includes(id);
  }

  static isCboBuiltInChannelGroup(id: number): boolean {
    return this.CBO_BUILT_IN_CHANNEL_GROUP_IDS.includes(id);
  }

  static register(key: string, channelGroup: ChannelGroupDTO) {
    const existChannelSetting = Array.from(this._values.values()).find(
      (value: ChannelGroupSetting) => value.config.id === channelGroup.id
    );

    if (existChannelSetting) {
      existChannelSetting.setName(channelGroup.name);
      existChannelSetting.setChannelType(channelGroup.channelType);
      // ECOPD-5566: 狀態也要跟著更新，否則渠道被停用後重新註冊時會沿用舊狀態
      existChannelSetting.setStatus(channelGroup.status);
      // ECOPD-5572: 自停旗標同理
      existChannelSetting.setArchiveFlags(channelGroup);
      return;
    }

    const newChannelGroupSetting = new ChannelGroupSetting(key, {
      ...channelGroup,
      status: channelGroup.status ?? ChannelGroupStatus.ACTIVE,
      // ECOPD-5572: **全前端唯一一處**把後端的兩個布林正規化成必填布林。
      // 之後所有讀取端都直接讀 config，不必再處理 undefined。
      pmaxArchived: channelGroup.pmaxArchived ?? false,
      effectiveArchived: channelGroup.effectiveArchived ?? false,
      defaultMargin: channelGroup.property.addonProps.defaultMargin,
      nonExpressPassCategories:
        channelGroup.property.addonProps.nonExpressPassCategories,
      brand: channelGroup.property.addonProps.brand ?? undefined,
      addonName: channelGroup.id.toString(),
      objectives: [L1ObjectObjective.AWARENESS],
      // 後端對「沒設定廣告類型」與「非 dynamic」的渠道會送 null，不是空陣列。
      campaignAdTypeSettings: (channelGroup.campaignAdTypeSettings ?? []).map(
        (setting) => ({
          ...setting,
          channelGroupId: channelGroup.id
        })
      )
    });
    console.debug(
      `Registered new channel group setting: ${newChannelGroupSetting.key}`
    );
  }

  /**
   * ECOPD-5572：取得新資料成功之後才動 registry，失敗時 registry 完全不動。
   *
   * 拿到資料後刪光非 static 渠道再整批重建，所以後端已經不存在的渠道會跟著消失。
   * **不要**改成「保留既有 key、只覆寫變動」—— 那會走 `register()` 的既存分支，
   * 而既存分支只更新 name / channelType / status 與停用旗標，
   * `campaignAdTypeSettings`、`defaultMargin` 這些都不會更新。
   */
  static async refresh(channelGroupManager: ChannelGroupManager) {
    const channelGroups = await channelGroupManager.getChannelGroups(true);

    const keysToDelete: string[] = [];
    this._values.forEach((setting, key) => {
      if (!this.STATIC_CHANNEL_GROUP_IDS.includes(setting.config.id)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => this._values.delete(key));

    channelGroups.forEach((channelGroup) => {
      this.register(channelGroup.channel, channelGroup);
    });
  }

  static async init(channelGroupManager: ChannelGroupManager) {
    const channelGroups = await channelGroupManager.getChannelGroups(true);
    channelGroups.forEach((channelGroup) => {
      this.register(channelGroup.channel, channelGroup);
    });
  }

  static getAddonNameOfChannel(channel: string): string | undefined {
    const channelSetting = this._values.get(channel);
    return channelSetting?.config.addonName;
  }

  /**
   * ECOPD-5566: 只含未停用渠道的 add-on 設定，供經銷商 add-on 表單使用。
   * `getAddonConfigs()` 維持回傳全部 —— 稽核紀錄與 add-on 檢視頁要靠它把
   * 停用渠道的 addon key 反查回渠道名，濾掉會印成 `[unknown channel id: X]`。
   */
  static getActiveAddonConfigs() {
    const activeAddonNames = new Set(
      this.activeValues().map((setting) => setting.config.addonName)
    );
    const allConfigs = this.getAddonConfigs();
    return Object.keys(allConfigs)
      .filter((addonName) => activeAddonNames.has(addonName))
      .reduce(
        (acc, addonName) => {
          acc[addonName] = allConfigs[addonName];
          return acc;
        },
        {} as ReturnType<typeof ChannelGroupSetting.getAddonConfigs>
      );
  }

  static getAddonConfigs() {
    return this.values().reduce(
      (acc, channelGroup) => {
        acc[channelGroup.config.addonName] = {
          channelName: channelGroup.config.name,
          channelId: channelGroup.config.id,
          defaultMargin: channelGroup.config.defaultMargin,
          needExpressPass: channelGroup.config.needExpressPass
        };
        return acc;
      },
      {} as {
        [key: string]: {
          channelName: string;
          channelId?: number;
          defaultMargin?: number;
          needExpressPass?: boolean;
        };
      }
    );
  }

  setName(name: string) {
    this.config.name = name;
  }

  setChannelType(channelType: ChannelType) {
    this.config.channelType = channelType;
  }

  setStatus(status: ChannelGroupStatus) {
    this.config.status = status ?? ChannelGroupStatus.ACTIVE;
  }

  /**
   * ECOPD-5572: 從後端 DTO 更新兩個停用旗標。
   * 與 `setStatus` 一樣，是給 `register()` 的既存分支用的 —— 渠道重新註冊時
   * 若不更新，旗標就會停在第一次註冊的舊值。
   */
  setArchiveFlags(channelGroup: ChannelGroupDTO) {
    this.config.pmaxArchived = channelGroup.pmaxArchived ?? false;
    this.config.effectiveArchived = channelGroup.effectiveArchived ?? false;
  }

  /**
   * ECOPD-5572: 此渠道是否被 **PMax 自己**停用。
   * **只**用來決定管理畫面的按鈕要顯示「停用」還是「重新啟用」。
   * 判斷渠道能不能被選 / 能不能編輯，一律用 `isEffectivelyArchived()`。
   */
  isPmaxArchived(): boolean {
    return this.config.pmaxArchived;
  }

  /**
   * ECOPD-5572: 此渠道在 PMax 端是否停用（合併判定：RMax 已封存 OR PMax 自停）。
   * 值由**後端單一計算**，這裡純粹照讀 —— 前端重算就會出現第二份定義。
   */
  isEffectivelyArchived(): boolean {
    return this.config.effectiveArchived;
  }

  getActiveCampaignAdTypes() {
    return this.config.campaignAdTypeSettings.filter(
      (setting) => setting.state === CampaignAdTypeState.ACTIVE
    );
  }

  /**
   * outdoor 渠道設定（**不含 BEACON**）。經銷商編輯頁的「廣告活動目標」就是靠這支
   * 決定要為哪些渠道渲染一整段開關。
   *
   * ⚠️ 與 isOutdoorChannel() 名字相近但集合不同（那支含 BEACON）。ECOPD-5581 新增
   * E_PAPER 時兩支都要加——只加 isOutdoorChannel 的話，電子紙的廣告活動目標會整段
   * 不出現，而且沒有任何錯誤訊息。
   */
  static getOutdoorChannelSettings() {
    return this.values().filter((value) =>
      [
        ChannelType.OUTDOOR,
        ChannelType.MOVABLE_OUTDOOR,
        ChannelType.E_PAPER
      ].includes(value.config.channelType)
    );
  }

  static getBeaconChannelSettings() {
    return this.values().filter((value) =>
      [ChannelType.BEACON].includes(value.config.channelType)
    );
  }

  /**
   * 這個渠道算不算 outdoor（**含 BEACON**）。
   *
   * ⚠️ 與 getOutdoorChannelSettings() 名字相近但集合不同（那支不含 BEACON）。
   * ECOPD-5581 新增 E_PAPER 時兩支都要加。
   *
   * 對電子紙而言加進來是維持現狀而非擴大：電子紙渠道在後端改送 E_PAPER 之前是
   * OUTDOOR，本來就會通過這一關（連帶 supportOutdoorTA / hasFixedDeliveryTargets），
   * 不加才會讓行為變掉。
   */
  isOutdoorChannel() {
    return ChannelGroupSetting.isOutdoorChannelType(this.config.channelType);
  }

  /**
   * 與 isOutdoorChannel() 同一個集合，給手上只有 channelType、沒有 registry 設定的地方用
   * （例如渠道編輯表單的 Formik values）。
   */
  static isOutdoorChannelType(channelType?: ChannelType) {
    return (
      channelType !== undefined &&
      [
        ChannelType.OUTDOOR,
        ChannelType.MOVABLE_OUTDOOR,
        ChannelType.BEACON,
        ChannelType.E_PAPER
      ].includes(channelType)
    );
  }

  supportOutdoorTA() {
    return (
      this.isOutdoorChannel() && this.config.channelType !== ChannelType.BEACON
    );
  }

  hasFixedDeliveryTargets() {
    return (
      this.isOutdoorChannel() &&
      this.config.channelType !== ChannelType.MOVABLE_OUTDOOR
    );
  }

  static isBeaconBannerType(bannerTypeId: number) {
    const creativeTypeSetting = this.creativeTypeSettings().get(bannerTypeId);
    if (!creativeTypeSetting) {
      return false;
    }
    return (
      this.fromId(creativeTypeSetting.channelGroupId).config.channelType ===
      ChannelType.BEACON
    );
  }

  static supportBeaconChannel(channelAddons: { [key: string]: boolean }) {
    const enabledChannelIds = Object.keys(channelAddons)
      .filter((key) => !isNaN(Number(key)) && channelAddons[key] === true)
      .map(Number);
    const beaconChannelIds = ChannelGroupSetting.getBeaconChannelSettings().map(
      (setting) => setting.config.id
    );
    return enabledChannelIds.some((id) => beaconChannelIds.includes(id));
  }

  getScreenDisplayTypeByCreativeType(creativeType: number): ScreenDisplayType {
    const adTypeSetting = this.config.campaignAdTypeSettings.find(
      (adTypeSetting) =>
        adTypeSetting.bannerTypes.some(
          (bannerTypeSetting) => bannerTypeSetting.bannerTypeId === creativeType
        )
    );
    if (!adTypeSetting || !adTypeSetting.screenDisplayType) {
      return ScreenDisplayType.SINGLE;
    }

    return adTypeSetting.screenDisplayType;
  }
}

/**
 * Resolve the outdoor channel's delivery unit (STORE / DEVICE). Returns
 * `undefined` for non-Outdoor adFormat, missing channel, or channels without a
 * configured deliveryUnit. Single source of truth for downstream
 * `getIsStoreUnit` / `getIsDeviceUnit` helpers.
 */
export const getOutdoorDeliveryUnit = (
  adFormat: string | undefined,
  channelGroupId: number | undefined
): DeliveryUnit | undefined => {
  if (adFormat !== 'Outdoor' || channelGroupId === undefined) {
    return undefined;
  }
  return ChannelGroupSetting.fromId(channelGroupId).config.deliveryUnit;
};

export const getIsStoreUnit = (
  adFormat: string | undefined,
  channelGroupId: number | undefined
): boolean =>
  getOutdoorDeliveryUnit(adFormat, channelGroupId) === DeliveryUnit.STORE;

export const getIsDeviceUnit = (
  adFormat: string | undefined,
  channelGroupId: number | undefined
): boolean =>
  getOutdoorDeliveryUnit(adFormat, channelGroupId) === DeliveryUnit.DEVICE;
